import React from 'react';
import translate from '@/utils/translate';
import type {CampaignProps} from '@/types/campaign';
import getNetworkDisplayName from '../utils/networkDisplayName';
import resolveCompensationType from '../utils/resolveCompensationType';
import styles from './CampaignBriefStats.module.scss';
import StatBox, {StatBoxProps} from './StatBox';

function formatPrice(value: number): string {
  return `$${value.toLocaleString()}`;
}

function formatFollowerMin(count: number): string {
  if (count >= 1000) {
    return `${Math.round(count / 1000)}K+`;
  }
  return `${count}+`;
}

interface CampaignBriefStatsProps {
  campaign: CampaignProps;
  maxShown?: number;
  testID?: string;
}

function CampaignBriefStats({
  campaign,
  maxShown = 1,
  testID = 'campaign-brief-stats',
}: CampaignBriefStatsProps) {
  const {
    suggestedCostForCreationsMin,
    suggestedCostForCreations,
    fixedCostForCreations,
    network,
    unlocksAt,
    preferredCreatorAgeDetail,
    preferredCreatorCountries,
    preferredCreatorStates,
    preferredCreatorCities,
    preferredCreatorGenders,
  } = campaign;

  const compensationType = resolveCompensationType(campaign);
  const stats: StatBoxProps[] = [];

  // Compensation
  if (
    compensationType === 'range' &&
    (suggestedCostForCreationsMin != null || suggestedCostForCreations != null)
  ) {
    const min =
      suggestedCostForCreationsMin != null
        ? formatPrice(suggestedCostForCreationsMin)
        : '';
    const max =
      suggestedCostForCreations != null
        ? formatPrice(suggestedCostForCreations)
        : '';
    stats.push({
      label: translate('SUGGESTED'),
      values: [min && max ? `${min}-${max}` : min || max],
      sublabel: translate('Bid Price'),
    });
  } else if (compensationType === 'fixed' && fixedCostForCreations != null) {
    stats.push({
      label: translate('FIXED'),
      values: [formatPrice(fixedCostForCreations)],
      sublabel: translate('Bid Price'),
    });
  }

  // Network + followers
  if (unlocksAt != null) {
    stats.push({
      label: getNetworkDisplayName(network),
      values: [formatFollowerMin(unlocksAt)],
      sublabel: translate('Followers'),
    });
  }

  // Age
  const {minAge, maxAge} = preferredCreatorAgeDetail ?? {};
  if (minAge != null || maxAge != null) {
    let ageValue: string;
    if (minAge != null && maxAge != null) {
      ageValue = `${minAge}-${maxAge}`;
    } else if (minAge != null) {
      ageValue = `${minAge}+`;
    } else {
      ageValue = `≤${maxAge}`;
    }
    stats.push({
      label: translate('AGE'),
      values: [ageValue],
      sublabel: translate('Years old'),
    });
  }

  // Location — countries first, then states, then cities
  const allLocations = [
    ...(preferredCreatorCountries ?? []),
    ...(preferredCreatorStates ?? []),
    ...(preferredCreatorCities ?? []),
  ]
    .map((l) => l.name)
    .filter(Boolean) as string[];

  if (allLocations.length) {
    stats.push({
      label: translate('LOCATION'),
      values: allLocations,
      maxShown,
    });
  }

  // Gender
  if (preferredCreatorGenders?.length) {
    const names = preferredCreatorGenders
      .map((g) => g.name)
      .filter(Boolean) as string[];
    stats.push({
      label: translate('Gender'),
      values: names,
      maxShown: 2,
    });
  }

  if (!stats.length) {
    return null;
  }

  return (
    <div className={styles.container} data-testid={testID}>
      <div className={styles.statsGrid}>
        {stats.map((stat) => (
          <StatBox key={stat.label} {...stat} />
        ))}
      </div>
    </div>
  );
}

export default CampaignBriefStats;
